import { gsT } from "../i18n";
import { generateConfig } from "src/gs-utils/core/utils";
import { API_URL_CONFIG_TYPE } from "src/gs-utils/core/types";
// 导入所有应用图标
import robotSvg from "./images/robot.svg";
import iamSvg from "./images/iam.svg";
import dataCenterSvg from "./images/data-center.svg";
import auditCenterSvg from "./images/audit-center.svg";
import applicationSvg from "./images/application.svg";
import developerSvg from "./images/developer.svg";
import downloadToolSvg from './images/download-tool.svg';
import parameterFlowManagementSvg from './images/parameter-flow-management.svg';
import { getOverseasRegion } from "src/gs-utils/core/config";
// 应用类型枚举
export enum AppType {
  INTERNAL = "internal",
  EXTERNAL = "external",
}

export enum PlatformAppType {
  IAM = "iam",
  ROBOT_CLOUD = "robot-cloud",
  OPEN_PLATFORM = "open-platform",
  AUDIT_CENTER = "audit-center",
  DEVELOPER_TOOLS = "developer-tools",
  DATA_CENTER = "data-center",
  APPLICATION_CENTER = "application-center",
  PARAMETER_FLOW_MANAGEMENT = "parameter-flow-management",
}

// 应用基础配置接口
export interface BaseAppConfig {
  name: string;
  key: string;
  applicationCode: string | boolean;
  regions?: string[];
  permissionCode: string | boolean;
  icon: string;
  backgroundColor: string;
  url: string;
  type: AppType;
}

export interface FilterPermissionAppsProps {
  applicationPermissionList?: string[];
  permissionCodeList?: string[];
  internalPerson?: boolean;
}

// 获取基础配置，并根据当前域名动态调整URL
const getProcessedConfig = () => {
  const config = generateConfig() as API_URL_CONFIG_TYPE;

  // 线上，根据当前域名动态调整URL
  if (typeof window !== 'undefined' && process.env.REACT_APP_NODE_ENV === 'prod') {
    const hostname = window.location.hostname;

    // 如果当前域名以 'service' 开头，则使用当前域名替换 serviceUrl
    if (hostname.startsWith('service')) {
      const domainSuffix = hostname.replace('service', '');

      return {
        ...config,
        serviceUrl: `https://service${domainSuffix}`,
        developerUrl: config.developerUrl.replace('.gs-robot.com', domainSuffix || '.gs-robot.com'),
      };
    }

    return config

  }

  return config;
};

const overseasRegion = getOverseasRegion();

const { serviceUrl, homeUrl, developerUrl } = getProcessedConfig();
// 应用基础配置映射
export const BASE_APP_CONFIGS: Record<string, BaseAppConfig> = {
  [PlatformAppType.ROBOT_CLOUD]: {
    name: gsT("robot_platform"),
    key: PlatformAppType.ROBOT_CLOUD,
    regions: ["eu", "sg", "na", "us"],
    applicationCode: "RobotCloudPlatform",
    permissionCode: true,
    icon: robotSvg,
    backgroundColor: "#3D7CFF",
    url: serviceUrl,
    type: AppType.EXTERNAL,
  },
  [PlatformAppType.IAM]: {
    name: 'IAM',
    key: PlatformAppType.IAM,
    regions: ["eu", "sg", "na", "us"],
    applicationCode: "IAM",
    permissionCode: true,
    icon: iamSvg,
    backgroundColor: "#4B64FF",
    url: serviceUrl + "/iam",
    type: AppType.EXTERNAL,
  },
  [PlatformAppType.DATA_CENTER]: {
    name: gsT("data_center"),
    key: PlatformAppType.DATA_CENTER,
    regions: [],
    applicationCode: "DataOpsPlatform",
    permissionCode: true,
    icon: dataCenterSvg,
    backgroundColor: "#4BB5FF",
    // 国内数据中心跳转地址不变，海外数据中心跳转云平台页面
    url: overseasRegion === 'CN' ? serviceUrl + "/data-center" : serviceUrl + "/#/dataPlatform/performanceMetrics",
    type: AppType.EXTERNAL,
  },
  [PlatformAppType.APPLICATION_CENTER]: {
    name: gsT("application_center"),
    key: PlatformAppType.APPLICATION_CENTER,
    applicationCode: true,
    permissionCode: true,
    regions: ["eu", "sg", "na", "us"],
    icon: applicationSvg,
    backgroundColor: "#FF9437",
    url: homeUrl,
    type: AppType.INTERNAL,
  },
  [PlatformAppType.PARAMETER_FLOW_MANAGEMENT]: {
    name: gsT("parameter_flow_management"),
    key: PlatformAppType.PARAMETER_FLOW_MANAGEMENT,
    applicationCode: true,
    permissionCode: true,
    regions: ["eu", "sg", "na", "us"],
    icon: parameterFlowManagementSvg,
    backgroundColor: "#FF9437",
    url: homeUrl + '/parameter-flow-management',
    type: AppType.INTERNAL,
  },
  [PlatformAppType.AUDIT_CENTER]: {
    name: gsT("audit_center"),
    key: PlatformAppType.AUDIT_CENTER,
    applicationCode: "AuditCenter",
    permissionCode: true,
    regions: [],
    icon: auditCenterSvg,
    backgroundColor: "#7F50F4",
    url: serviceUrl + "/audit-center",
    type: AppType.EXTERNAL,
  },
  [PlatformAppType.OPEN_PLATFORM]: {
    name: gsT("developer_platform"),
    key: PlatformAppType.OPEN_PLATFORM,
    regions: ["eu", "sg", "na", "us"],
    applicationCode: "DeveloperCenter",
    permissionCode: true,
    icon: developerSvg,
    backgroundColor: "#23C27C",
    url: developerUrl,
    type: AppType.EXTERNAL,
  },
  [PlatformAppType.DEVELOPER_TOOLS]: {
    name: gsT("developer_tools"),
    regions: ["eu", "sg", "na", "us"],
    key: PlatformAppType.DEVELOPER_TOOLS,
    applicationCode: true,
    permissionCode: "ToolDownload.view",
    icon: downloadToolSvg,
    backgroundColor: "#23C2BB",
    url: developerUrl + "/MaintenanceTools",
    type: AppType.EXTERNAL,
  },
};

// 获取基础配置的辅助函数
export const getBaseAppConfig = (key: string): BaseAppConfig | undefined => {
  return BASE_APP_CONFIGS[key];
};

// 获取所有基础配置
export const getAllBaseAppConfigs = (): Record<string, BaseAppConfig> => {
  return BASE_APP_CONFIGS;
};

export const BASE_APP_CONFIGS_LIST = Object.values(BASE_APP_CONFIGS);

// 配置合并函数
export const mergeAppConfig = (businessConfig: BaseAppConfig): BaseAppConfig => {
  const baseConfig = BASE_APP_CONFIGS[businessConfig.key];

  if (!baseConfig) {
    throw new Error(`Base config not found for key: ${businessConfig.key}`);
  }

  return {
    ...baseConfig,
    ...businessConfig,
    // 确保基础配置不被意外覆盖为undefined
    icon: businessConfig.icon ?? baseConfig.icon,
    backgroundColor: businessConfig.backgroundColor ?? baseConfig.backgroundColor,
    url: businessConfig.url ?? baseConfig.url,
    type: businessConfig.type ?? baseConfig.type,
  };
};

export const getAppConfigs = (appConfigs: BaseAppConfig[]): BaseAppConfig[] => {
  return appConfigs.map(mergeAppConfig);
};

// 筛选regions逻辑
export const filterRegionApps = (appConfigs: BaseAppConfig[]): BaseAppConfig[] => {
  const region = process.env.REACT_APP_REGION || "";
  // 国内不筛选region
  if (!region) return appConfigs;
  // 海外筛选region
  return appConfigs.filter((app) => app.regions?.includes(region));
}

// 筛选权限逻辑, 包含internalPerson, permissionCode, application 等逻辑
export const filterPermissionApps = (appConfigs: BaseAppConfig[], { applicationPermissionList, permissionCodeList, internalPerson }: FilterPermissionAppsProps): BaseAppConfig[] => {
  // 先筛选region
  let resultApps = filterRegionApps(appConfigs);
  // 先筛选内外部, 外部情况下不显示INTERNAL应用
  const externalApps = resultApps.filter((app) => app.type === AppType.EXTERNAL);
  // 外部情况下不显示INTERNAL应用
  if (!internalPerson) {
    resultApps = externalApps;
  }

  // 再筛选应用权限
  resultApps = resultApps.filter((app) => {
    if (!app.applicationCode) return false;
    // 设置布尔值为true
    if (typeof app.applicationCode === 'boolean' && app.applicationCode) return true;
    return applicationPermissionList?.includes(app.applicationCode);
  });

  // 最后筛选权限点权限
  resultApps = resultApps.filter((app) => {
    if (!app.permissionCode) return false;
    // 设置布尔值为true
    if (typeof app.permissionCode === 'boolean' && app.permissionCode) return true;
    return permissionCodeList?.includes(app.permissionCode);
  });

  return resultApps;
}


